import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Input,
  InputNumber,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { hasPermission } from "../../utils/permissions";
import type { ClosingReport } from "../../types";

const { Title, Text } = Typography;

const DailyClosePage: React.FC = () => {
  const canClose = hasPermission("finance:close");

  const [date, setDate] = useState<Dayjs>(dayjs());
  const [report, setReport] = useState<ClosingReport | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [counted, setCounted] = useState<number | null>(null);
  const [notes, setNotes] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const iso = date.format("YYYY-MM-DD");
      const [rep, closings] = await Promise.all([
        financeService.getClosingReport(iso),
        financeService.getClosings(90),
      ]);
      setReport(rep);
      setHistory(closings);
      setCounted(rep.cash?.counted ?? null);
      setNotes(rep.notes ?? undefined);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load the closing report");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const close = async () => {
    setBusy(true);
    try {
      await financeService.closeDay({
        business_date: date.format("YYYY-MM-DD"),
        counted_cash: counted,
        notes,
      });
      message.success(`${date.format("MMM D, YYYY")} closed`);
      await load();
    } catch (e: any) {
      message.error(e?.message || "Failed to close the day");
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await financeService.reopenDay(date.format("YYYY-MM-DD"), "Reopened from the admin panel");
      message.success("Day reopened — posting is allowed again");
      await load();
    } catch (e: any) {
      message.error(e?.message || "Failed to reopen the day");
    } finally {
      setBusy(false);
    }
  };

  const variance = report && counted !== null ? counted - report.cash.expected : null;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Daily Close
          </Title>
          <Text type="secondary">Z-report: totals, cash count and posting lock for a business date</Text>
        </div>
        <Space wrap>
          <DatePicker
            value={date}
            allowClear={false}
            disabledDate={(d) => d.isAfter(dayjs(), "day")}
            onChange={(d) => d && setDate(d)}
          />
          {!canClose && <Tag color="orange">read-only (needs finance:close)</Tag>}
        </Space>
      </Space>

      {error && (
        <Alert type="error" showIcon title="Failed to load" description={error} style={{ marginBottom: 16 }} />
      )}

      {report?.is_closed && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          title="This business date is closed"
          description={
            <Space wrap>
              <span>
                Closed by {report.closed_by || "unknown"}
                {report.closed_at ? ` · ${dayjs(report.closed_at).format("MMM D, YYYY HH:mm")}` : ""}
              </span>
              {canClose && (
                <Button size="small" danger onClick={reopen} loading={busy}>
                  Reopen day
                </Button>
              )}
            </Space>
          }
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic title="Income" value={report?.income ?? 0} precision={2} suffix="LKR" valueStyle={{ color: "#52c41a" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic title="Expense" value={report?.expense ?? 0} precision={2} suffix="LKR" valueStyle={{ color: "#a5171f" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Net profit"
              value={report?.net_profit ?? 0}
              precision={2}
              suffix="LKR"
              valueStyle={{ color: (report?.net_profit ?? 0) >= 0 ? "#b79a68" : "#a5171f" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic title="Expected cash" value={report?.cash.expected ?? 0} precision={2} suffix="LKR" />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Cash count" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Opening cash">{formatLkr(report?.cash.opening ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="Received">{formatLkr(report?.cash.received ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="Paid out">{formatLkr(report?.cash.paid ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="Expected">{formatLkr(report?.cash.expected ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="Counted">
                {report?.cash.counted === null || report?.cash.counted === undefined
                  ? "—"
                  : formatLkr(report.cash.counted)}
              </Descriptions.Item>
              <Descriptions.Item label="Variance">
                {report?.cash.variance === null || report?.cash.variance === undefined ? (
                  "—"
                ) : (
                  <Text type={Number(report.cash.variance) === 0 ? "success" : "warning"}>
                    {formatLkr(Number(report.cash.variance))}
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Notes" span={2}>
                {report?.notes || "—"}
              </Descriptions.Item>
            </Descriptions>

            {canClose && !report?.is_closed && (
              <Space direction="vertical" style={{ width: "100%", marginTop: 16 }} size={8}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={100}
                  precision={2}
                  placeholder="Counted cash in hand"
                  value={counted}
                  onChange={setCounted}
                />
                {variance !== null && (
                  <Text type={Math.abs(variance) < 0.01 ? "success" : "warning"}>
                    Variance: {formatLkr(variance)}
                  </Text>
                )}
                <Input.TextArea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Closing notes (optional)"
                />
                <Space wrap>
                  <Button type="primary" loading={busy} onClick={close}>
                    Close {date.format("MMM D, YYYY")}
                  </Button>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Posting to this date is blocked once closed.
                  </Text>
                </Space>
              </Space>
            )}
          </Card>

          <Card title="Recent closings" size="small">
            <Table
              rowKey="business_date"
              size="small"
              loading={loading}
              dataSource={history}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              scroll={{ x: 620 }}
              columns={[
                {
                  title: "Date",
                  dataIndex: "business_date",
                  width: 120,
                  render: (v: string) => dayjs(String(v).slice(0, 10)).format("MMM D, YYYY"),
                },
                {
                  title: "Income",
                  dataIndex: "income_total",
                  align: "right" as const,
                  render: (v: number) => formatLkr(Number(v || 0)),
                },
                {
                  title: "Expense",
                  dataIndex: "expense_total",
                  align: "right" as const,
                  render: (v: number) => formatLkr(Number(v || 0)),
                },
                {
                  title: "Net",
                  dataIndex: "net_profit",
                  align: "right" as const,
                  render: (v: number) => formatLkr(Number(v || 0)),
                },
                {
                  title: "Variance",
                  dataIndex: "variance",
                  align: "right" as const,
                  render: (v: number | null) =>
                    v === null || v === undefined ? (
                      "—"
                    ) : (
                      <Text type={Number(v) === 0 ? "success" : "warning"}>{formatLkr(Number(v))}</Text>
                    ),
                },
                { title: "Closed by", dataIndex: "closed_by_name", ellipsis: true, render: (v: string) => v || "—" },
              ]}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No days closed yet" />,
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Z-report detail" size="small">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Business date">
                {report ? dayjs(report.business_date).format("dddd, MMM D, YYYY") : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {report?.is_closed ? <Tag color="green">closed</Tag> : <Tag color="orange">open</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Income">{formatLkr(report?.income ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="Cost of goods & expense">
                {formatLkr(report?.expense ?? 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Net profit">{formatLkr(report?.net_profit ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="Cash movement">
                {formatLkr((report?.cash.received ?? 0) - (report?.cash.paid ?? 0))}
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              title="What closing does"
              description="Closing locks the business date: new vouchers, approvals and payments for that date are rejected with 409 until the day is reopened. Reopen only with a reason."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DailyClosePage;
